// ============================================================
// Capa de datos.
//
// Traduce entre las filas de Postgres y las formas que la app ya
// usa en React (semanas → sesiones → ejercicios). Toda la app sigue
// hablando su idioma; si mañana cambia la base, se cambia acá y
// no en cuarenta lugares de la interfaz.
// ============================================================

import { leer, insertar, actualizar, borrar, guardarOInsertar, llamarFuncion, sesionActual } from "./supabase.js";

const miId = () => sesionActual()?.user?.id;

// ---------- Perfil ----------

export async function cargarPerfil() {
  const id = miId();
  if (!id) return null;
  const [perfil] = await leer("perfiles", { eq: { id }, select: "*" });
  return perfil || null;
}

export async function actualizarPerfil(cambios) {
  const id = miId();
  const [p] = await actualizar("perfiles", { id }, cambios);
  return p;
}

// Datos privados: solo los ve el propio alumno y el coach. Viven
// aparte del perfil porque el perfil es visible para todos (lo
// necesitan el ranking y las insignias) y esto no debería serlo.
export async function cargarDatosPrivados(alumnoId) {
  const id = alumnoId || miId();
  if (!id) return null;
  const [fila] = await leer("perfiles_privados", { eq: { alumno_id: id }, select: "*" });
  return fila || null;
}

// Se usa insertar-o-actualizar porque la fila puede no existir: los
// perfiles creados a mano desde el editor SQL no pasaron por el
// disparador que la crea.
export const guardarDatosPrivados = (alumnoId, datos) =>
  guardarOInsertar("perfiles_privados", {
    alumno_id: alumnoId || miId(),
    genero: datos.genero || null,
    fecha_nacimiento: datos.fechaNacimiento || null,
    sesiones_semana: datos.sesionesSemana ? Number(datos.sesionesSemana) : null,
    sigue_ciclo: !!datos.sigueCiclo,
    ciclo_ultimo_periodo: datos.cicloUltimoPeriodo || null,
    ciclo_duracion: datos.cicloDuracion ? Number(datos.cicloDuracion) : null,
    notas_coach: datos.notasCoach ?? null,
    actualizado_en: new Date().toISOString(),
  }, "alumno_id");

// Nombre y apellido viven en el perfil público.
// Devuelve la fila actualizada, igual que actualizarPerfil. Antes una
// devolvía la fila y la otra una lista, y en la app se leía la lista
// donde había un objeto: eso rompía el guardado sin dejar rastro.
export async function guardarNombre(nombre, apellido) {
  const [fila] = await actualizar("perfiles", { id: miId() }, { nombre, apellido });
  return fila;
}

// ---------- Alumnos (vista del coach) ----------

// El coach ve la lista con el puntaje del torneo: sin esto su
// ranking mostraba un número distinto al del alumno.
export async function cargarAlumnos() {
  const perfiles = await leer("perfiles", {
    eq: { rol: "alumno", activo: true },
    select: "id,nombre,apellido,foto_url,exp,nivel,vacaciones,admin",
    order: "nombre.asc",
  });
  if (!perfiles.length) return [];

  // El plan se organiza por SEMANAS ("Semana 4 · JULIO"), no por fechas
  // de calendario: la sesión recién recibe fecha cuando el alumno la
  // cierra. Contar por fecha dejaba afuera todo lo recién asignado.
  const idsActivos = new Set(perfiles.map((p) => p.id));
  const semanas = await leer("semanas", {
    select: "id,alumno_id,orden,visible_desde,creado_en", order: "orden.desc",
  });

  // ¿Ya tiene planificada la semana que viene? Su semana del plan
  // arranca el lunes en que se hace visible (o en que se creó); si eso
  // cae de viernes a domingo, el lunes siguiente. Igual que el ranking.
  const lunesAR = (ms) => {
    const d = new Date(ms - 3 * 3600e3);
    const dow = (d.getUTCDay() + 6) % 7;
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - dow);
  };
  const lunesPlan = (w) => {
    const ms = new Date(w.visible_desde || w.creado_en).getTime();
    const d = new Date(ms - 3 * 3600e3);
    const dow = (d.getUTCDay() + 6) % 7;
    return lunesAR(ms) + (dow >= 4 ? 7 * 864e5 : 0);
  };
  const lunesHoy = lunesAR(Date.now());
  const proxima = {};
  for (const w of semanas) {
    if (w.alumno_id in proxima) continue;
    proxima[w.alumno_id] = lunesPlan(w) > lunesHoy
      ? { desde: w.visible_desde && new Date(w.visible_desde).getTime() > Date.now() ? w.visible_desde : null }
      : null;
  }

  // Última semana de cada alumno activo, de las que ya están en curso:
  // una programada (o armada el fin de semana) para el lunes todavía no
  // es su semana, y contarla mostraba "0 de 2" a quien ya había
  // cumplido la actual.
  const ultima = {};
  for (const w of semanas) {
    if (!idsActivos.has(w.alumno_id)) continue;
    if (lunesPlan(w) > lunesHoy) continue;
    if (!(w.alumno_id in ultima)) ultima[w.alumno_id] = w.id;
  }
  const idsUltimas = Object.values(ultima);

  let sesiones = [];
  if (idsUltimas.length) {
    sesiones = await leer("sesiones", {
      select: "id,estado,semana_id",
      semana_id: `in.(${idsUltimas.join(",")})`,
    });
  }

  const porSemana = {};
  for (const s of sesiones) {
    porSemana[s.semana_id] = porSemana[s.semana_id] || { asignadas: 0, hechas: 0 };
    porSemana[s.semana_id].asignadas += 1;
    if (s.estado === "Completado") porSemana[s.semana_id].hechas += 1;
  }

  // El puntaje sale de la MISMA función que usa el alumno.
  //
  // Antes esto leía la vista `ranking`, que no incluye los puntos de
  // arrastre: el coach veía un ranking y sus alumnos otro. Una sola
  // fuente evita que vuelvan a separarse.
  const puntos = {};
  try {
    const filas = await llamarFuncion("ranking_torneo");
    for (const r of (Array.isArray(filas) ? filas : [])) puntos[r.alumno_id] = r;
  } catch { /* si falla, el ranking queda en cero pero la lista carga */ }

  return perfiles.map((p) => {
    const conteo = porSemana[ultima[p.id]] || { asignadas: 0, hechas: 0 };
    const r = puntos[p.id];
    return {
      id: p.id,
      name: `${p.nombre} ${p.apellido}`.trim(),
      photo: p.foto_url,
      exp: p.exp,
      vacaciones: !!p.vacaciones,
      semanaSesiones: conteo.asignadas,
      proximaLista: !!proxima[p.id],
      proximaDesde: proxima[p.id]?.desde || null,
      semanaHechas: conteo.hechas,
      score: r?.puntaje ?? 0,
      puntosPrevios: r?.puntos_previos ?? 0,
      puntosApp: r?.puntos_app ?? 0,
      puntosSemana: r?.puntos_semana ?? 0,
      hechasSemana: r?.hechas_semana ?? 0,
      metaSemana: r?.meta_semana ?? 0,
      sessionsDone: r?.sesiones_hechas ?? 0,
      sessionsTotal: r?.sesiones_totales ?? 0,
    };
  });
}

// Dar de baja a un alumno. No se borra la fila: se marca inactivo.
// Borrarlo de verdad se llevaría por delante su historial completo,
// y en un año puede volver. Además pierde el acceso: sin perfil
// activo, las reglas de la base le niegan todo.
export const darDeBajaAlumno = (id) =>
  actualizar("perfiles", { id }, { activo: false });

export const reactivarAlumno = (id) =>
  actualizar("perfiles", { id }, { activo: true });

// Borrar definitivo. Se elimina el perfil, y con él —por cascada— sus
// semanas, sesiones, registros, EXP e insignias. La cuenta de acceso
// la borra una función del servidor, que es la única con permiso.
export async function borrarAlumnoDefinitivo(id) {
  const r = await fetch("/.netlify/functions/borrar-alumno", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sesionActual()?.access_token || ""}`,
    },
    body: JSON.stringify({ id }),
  });
  // Si la función no está publicada, Netlify devuelve el index.html
  // en vez de JSON: el error quedaba vacío y el cartel decía solo
  // "no se pudo", sin pista de qué revisar.
  const texto = await r.text();
  let cuerpo = {};
  try { cuerpo = JSON.parse(texto); } catch {
    if (r.status === 404 || texto.trimStart().startsWith("<")) {
      throw new Error("La función del servidor no está publicada. " +
        "Revisá que netlify.toml tenga functions = \"netlify/functions\".");
    }
    throw new Error("Respuesta inesperada del servidor");
  }
  if (!r.ok) throw new Error(cuerpo.error || "No se pudo eliminar");
  return cuerpo;
}

export const cargarInactivos = () =>
  leer("perfiles", {
    eq: { rol: "alumno", activo: false },
    select: "id,nombre,apellido,foto_url,exp",
    order: "nombre.asc",
  });

// ---------- Semanas, sesiones y ejercicios ----------

// Una sola consulta anidada trae el árbol completo. Evita el
// problema clásico de pedir las sesiones de cada semana en un bucle.
const ARBOL = `
  id,nombre,periodo,orden,
  sesiones(
    id,nombre,orden,estado,fecha,dia,duracion_min,borg_rpe,musica_url,nota_semanal,
    fuerza_percibida,puntaje,carga,completada_en,compartida,
    sesion_ejercicios(
      id,orden,ejercicio_nombre,con_que,metrica,series,reps,
      rir_consigna,peso_usado,rir_tuyo,comentarios,
      video_alumno_url,video_url,gif_url,timer,biserie,modo,descanso,nota_coach,tecnica,tecnica_config,peso_sugerido,finalizador,seccion,comentario_foto,nota_coach_foto,mostrar_libras,bloque,ocultos,resultado_series,reps_por_lado,
      emom_bloques,emom_reps,comentario_previo,nota_coach_previa,emom_previo,
      series_previa,reps_previa,peso_sug_previo,rir_cons_previo,peso_previa,rir_previa,
      resultado_previo
    )
  )
`.replace(/\s+/g, "");

export async function cargarSemanas(alumnoId) {
  // Orden ascendente: la app toma la ÚLTIMA de la lista como la
  // semana en curso. Con orden descendente tomaba la primera que se
  // creó, y el alumno veía una semana vieja como si fuera la actual.
  // Con la hora de publicación (migración 077). Si la base todavía no
  // la tiene, se pide como antes: subir la app sin correr la
  // migración no puede dejar a nadie sin ver su plan.
  let filas;
  try {
    filas = await leer("semanas", {
      eq: { alumno_id: alumnoId },
      select: `creado_en,visible_desde,${ARBOL}`,
      order: "orden.asc",
    });
  } catch (e) {
    filas = await leer("semanas", {
      eq: { alumno_id: alumnoId },
      select: ARBOL,
      order: "orden.asc",
    });
  }
  return filas.map(aFormatoApp);
}

function aFormatoApp(w) {
  return {
    id: w.id,
    name: w.nombre,
    period: w.periodo,
    creadoEn: w.creado_en || null,
    visibleDesde: w.visible_desde || null,
    sessions: (w.sesiones || [])
      .sort((a, b) => a.orden - b.orden)
      .map((s) => ({
        id: s.id,
        name: s.nombre,
        estado: s.estado,
        fecha: s.fecha,
        day: s.dia,
        duracion: s.duracion_min ? String(s.duracion_min) : "",
        borgRPE: s.borg_rpe,
        musicaUrl: s.musica_url || "",
        notaSemanal: s.nota_semanal || "",
        fuerzaPercibida: s.fuerza_percibida,
        puntaje: s.puntaje,
        carga: s.carga,
        completadaEn: s.completada_en,
        compartida: !!s.compartida,
        exercises: (s.sesion_ejercicios || [])
          .sort((a, b) => a.orden - b.orden)
          .map((e) => ({
            id: e.id,
            // Cadena vacía y no nulo: la tarjeta recién creada todavía
            // no tiene ejercicio elegido, y el buscador espera texto.
            ejercicio: e.ejercicio_nombre || "",
            conQue: e.con_que || "",
            metric: e.metrica || "RIR",
            series: e.series || "",
            reps: e.reps || "",
            rirConsigna: e.rir_consigna || "",
            pesoUsado: e.peso_usado || "",
            rirTuyo: e.rir_tuyo || "",
            comentarios: e.comentarios || "",
            videoAlumno: e.video_alumno_url || "",
            videoUrl: e.video_url || "",
            gifUrl: e.gif_url || "",
            timer: e.timer || null,
      repsPorLado: !!e.reps_por_lado,
            biserie: e.biserie ?? null,
            modo: e.modo || "reps",
            descanso: e.descanso ?? null,
            notaCoach: e.nota_coach || "",
            tecnica: e.tecnica || "normal",
            tecnicaConfig: e.tecnica_config || null,
            pesoSugerido: e.peso_sugerido || "",
            finalizador: !!e.finalizador,
            seccion: e.seccion || (e.finalizador ? "finalizador" : "principal"),
            bloque: e.bloque || "",
            ocultos: Array.isArray(e.ocultos) ? e.ocultos : [],
            emomBloques: e.emom_bloques,
            emomReps: e.emom_reps,
            comentarioPrevio: e.comentario_previo || "",
            seriesPrevia: e.series_previa || "",
            repsPrevia: e.reps_previa || "",
            pesoSugPrevio: e.peso_sug_previo || "",
            rirConsPrevio: e.rir_cons_previo || "",
            resultadoPrevio: e.resultado_previo || null,
            notaCoachPrevia: e.nota_coach_previa || "",
            emomPrevio: e.emom_previo || "",
            mostrarLibras: !!e.mostrar_libras,
            resultadoSeries: Array.isArray(e.resultado_series) ? e.resultado_series : null,
            comentarioFoto: e.comentario_foto || "",
            notaCoachFoto: e.nota_coach_foto || "",
            pesoPrevia: "",
            rirPrevia: "",
          })),
      })),
  };
}

// ---------- Lo que registra el alumno ----------

export const guardarRegistroEjercicio = (id, campos) =>
  actualizar("sesion_ejercicios", { id }, {
    peso_usado: campos.pesoUsado,
    rir_tuyo: campos.rirTuyo,
    comentarios: campos.comentarios,
    ...(campos.comentarioFoto !== undefined && { comentario_foto: campos.comentarioFoto || null }),
    ...(campos.resultadoSeries !== undefined && { resultado_series: campos.resultadoSeries || null }),
    // Solo si viene en este guardado. Antes se escribía siempre: un
    // guardado que no lo incluyera —porque la copia en pantalla se
    // quedó vieja— lo ponía en null y BORRABA el video que el alumno
    // ya había subido.
    ...(campos.videoAlumno !== undefined && {
      video_alumno_url: campos.videoAlumno || null }),
    ...(campos.timer !== undefined && { timer: campos.timer }),
    actualizado_en: new Date().toISOString(),
  });

// Campos que prescribe el coach. Se guardan por su propio camino
// porque el permiso de la tabla no se los deja escribir a nadie:
// eso es lo que impide que el alumno cambie su propia consigna.
const CAMPOS_COACH = ["ejercicio", "conQue", "metric", "series", "reps",
  "rirConsigna", "pesoSugerido", "modo", "tecnica", "descanso", "biserie",
  "tecnicaConfig", "timer", "mostrarLibras", "seccion", "bloque", "ocultos",
  "repsPorLado"];

// La observación del coach y su foto NO van por acá: se guardan con
// guardar_nota_coach, que escribe las dos columnas juntas. Estaban en
// la lista de arriba y la función de prescripción las rechazaba: por
// eso fallaban TODOS los cambios de esa tanda con un error 400.

export const esCampoDelCoach = (campo) => CAMPOS_COACH.includes(campo);

// El orden va por su propia función: mandarlo campo por campo hacía
// que dos ejercicios compartieran posición a mitad de camino, y la
// base rechazaba la tanda entera.
export const reordenarEjercicios = (sesionId, ids) =>
  llamarFuncion("reordenar_ejercicios", { p_sesion: sesionId, p_ids: ids });

export const renombrarBloque = (sesionId, seccion, viejo, nuevo) =>
  llamarFuncion("renombrar_bloque", {
    p_sesion: sesionId, p_seccion: seccion,
    p_viejo: viejo || "", p_nuevo: nuevo });

export const guardarPrescripcion = (id, datos) =>
  llamarFuncion("guardar_prescripcion", { p_ejercicio: id, p_datos: datos });

export const agregarEjercicioSesion = (sesionId, datos) =>
  llamarFuncion("agregar_ejercicio_sesion", { p_sesion: sesionId, p_datos: datos });

export const quitarEjercicioSesion = (id) =>
  llamarFuncion("quitar_ejercicio_sesion", { p_ejercicio: id });

// Cierra la sesión y devuelve la EXP calculada POR LA BASE.
// El navegador no decide los puntos: solo informa lo que pasó.
// La observación del coach se guarda por separado: el alumno no
// puede escribirla, así que no va en la cola del registro.
// Pasa por una función de la base: el permiso por columnas de la
// tabla no deja escribir esta columna a nadie, ni al coach.
export const guardarNotaCoach = (id, nota, foto) =>
  llamarFuncion("guardar_nota_coach",
    { p_ejercicio: id, p_nota: nota || null, p_foto: foto || null });

export const cerrarSesion = (id, datos) =>
  llamarFuncion("cerrar_sesion", {
    p_sesion: id,
    p_dia: datos.dia || null,
    p_duracion: datos.duracion ? Number(datos.duracion) : null,
    p_borg: datos.borgRPE || null,
    p_fuerza: datos.fuerzaPercibida || null,
    p_fecha: datos.fecha || null,
  });


// ---------- Lo que arma el coach ----------

// Crea la semana y devuelve su id. El `orden` se calcula como el
// siguiente disponible para que las semanas nuevas queden arriba.
// El período en mayúsculas siempre: si se guarda con otra grafía, la
// semana aparece en una carpeta aparte aunque sea el mismo mes.
const mesNormal = (t) => String(t || "").trim().toUpperCase().replace(/\s+/g, " ");

export async function crearSemana(alumnoId, nombre, periodo, visibleDesde = null) {
  periodo = mesNormal(periodo);
  const previas = await leer("semanas", {
    eq: { alumno_id: alumnoId }, select: "orden", order: "orden.desc", limit: 1,
  });
  const [fila] = await insertar("semanas", {
    alumno_id: alumnoId,
    nombre,
    periodo,
    orden: (previas[0]?.orden ?? 0) + 1,
    creado_por: miId(),
    // Solo si se programa: sin la migración 077 la columna no existe,
    // y mandarla vacía haría fallar también las semanas "para ahora".
    ...(visibleDesde && { visible_desde: visibleDesde }),
  });
  return fila;
}

// Crea la sesión y sus ejercicios en dos pasos: primero la sesión
// para tener su id, después los ejercicios en una sola llamada.
export async function crearSesion(semanaId, sesion) {
  const previas = await leer("sesiones", {
    eq: { semana_id: semanaId }, select: "orden", order: "orden.desc", limit: 1,
  });
  const [fila] = await insertar("sesiones", {
    semana_id: semanaId,
    nombre: sesion.name,
    orden: (previas[0]?.orden ?? -1) + 1,
    estado: sesion.estado || "Pendiente",
    dia: sesion.day || null,
    // Desde el planificador: la fecha del día elegido, la nota de la
    // semana y la música. Solo si vienen.
    ...(sesion.fecha && { fecha: sesion.fecha }),
    ...(sesion.notaSemanal && { nota_semanal: sesion.notaSemanal }),
    ...(sesion.musicaUrl && { musica_url: sesion.musicaUrl }),
  });

  const ejercicios = (sesion.exercises || [])
    .filter((e) => e.ejercicio)
    .map((e, i) => ({
      sesion_id: fila.id,
      orden: i,
      ejercicio_nombre: e.ejercicio || null,
      con_que: e.conQue || null,
      metrica: e.metric || "RIR",
      series: e.series || null,
      reps: e.reps || null,
      rir_consigna: e.rirConsigna || null,
      video_url: e.videoUrl || null,
      gif_url: e.gifUrl || null,
      timer: e.timer || null,
      reps_por_lado: !!e.repsPorLado,
      biserie: e.biserie ?? null,
      modo: e.modo || "reps",
      descanso: e.descanso ?? null,
      nota_coach: e.notaCoach || null,
      tecnica: e.tecnica || "normal",
      tecnica_config: e.tecnicaConfig || null,
      peso_sugerido: e.pesoSugerido || null,
      mostrar_libras: !!e.mostrarLibras,
      finalizador: !!e.finalizador,
      seccion: e.seccion || (e.finalizador ? "finalizador" : "principal"),
      bloque: e.bloque || null,
      ocultos: Array.isArray(e.ocultos) ? e.ocultos : [],
      // Lo que escribió la semana pasada viaja como REFERENCIA, no
      // como comentario nuevo: el campo para escribir arranca vacío y
      // lo de antes queda a la vista arriba.
      // El comentario YA NO se copia: vive en el hilo del ejercicio y
      // se ve solo desde cualquier semana. Copiarlo hacía que se
      // arrastrara lo que no correspondía.
      comentario_previo: null,
      // La nota del coach NO viaja: es tuya, la escribiste para ESA
      // sesión. Arrastrarla hace que parezca una respuesta nueva a un
      // comentario que el alumno todavía no escribió.
      nota_coach_previa: null,
      // La prescripción y lo que cargó, para tener de referencia al
      // armar la semana nueva. Sin esto la semana duplicada no dice
      // de dónde salió cada número.
      // Desde el planificador la consigna nueva ya viene cambiada: la de
      // la semana pasada llega aparte en _pedido. Sin esto, la "previa"
      // mostraría el número nuevo como si fuera el viejo.
      series_previa: (e._pedido ? e._pedido.series : e.series) || null,
      reps_previa: (e._pedido ? e._pedido.reps : e.reps) || null,
      peso_sug_previo: (e._pedido ? e._pedido.peso : e.pesoSugerido) || null,
      peso_previa: e.pesoUsado || null,
      rir_cons_previo: (e._pedido ? e._pedido.rir : e.rirConsigna) || null,
      rir_previa: e.rirTuyo || null,
      // El piramidal: la escalera completa, no un número.
      resultado_previo: e.resultadoSeries || null,
      emom_previo: (e.emomBloques != null)
        ? `${e.emomBloques}|${e.emomReps || 0}` : null,
    }));

  // Si los ejercicios no entran, la sesión no queda a medias.
  //
  // Antes la sesión ya estaba creada y el error llegaba después: en el
  // plan aparecía una sesión vacía, y al reintentar se sumaba otra.
  let creados = [];
  if (ejercicios.length) {
    try {
      creados = await insertar("sesion_ejercicios", ejercicios);
    } catch (e) {
      await borrar("sesiones", { id: fila.id }).catch(() => {});
      throw e;
    }
  }

  // Los ejercicios con su id REAL, para que la app reemplace los
  // temporales que inventó al dibujarlos.
  //
  // Sin esto, los cambios que hacía el coach sobre una sesión recién
  // agregada viajaban a un id que no existe: no se guardaba nada y el
  // alumno seguía viendo la plantilla original.
  return { ...fila,
    ejercicios: (Array.isArray(creados) ? creados : []).sort((a, b) => a.orden - b.orden) };
}

export const renombrarSesion = async (id, cambios) => {
  // El nombre va por función: la columna no la puede escribir nadie
  // con el rol de siempre, ni el coach. Se acepta `nombre` o `name`
  // porque la app usaba los dos, y el que no coincidía se descartaba
  // en silencio: decía "guardado" y no guardaba.
  const nombre = cambios.nombre ?? cambios.name;
  if (nombre !== undefined) {
    await llamarFuncion("renombrar_sesion", { p_sesion: id, p_nombre: nombre });
  }

  const resto = {
    ...(cambios.day !== undefined && { dia: cambios.day || null }),
    // La fecha real del día al que se arrastró. El ranking decide la
    // semana por la fecha, no por el nombre del día: sin esto, una
    // sesión arrastrada al domingo y hecha el lunes contaba para la
    // semana equivocada.
    ...(cambios.fecha !== undefined && { fecha: cambios.fecha || null }),
    ...(cambios.completadaEn !== undefined && { completada_en: cambios.completadaEn || null }),
    ...(cambios.estado !== undefined && { estado: cambios.estado }),
  };

  if (Object.keys(resto).length) await actualizar("sesiones", { id }, resto);
  return { ok: true };
};

export const borrarSemana = (id) => borrar("semanas", { id });

// Cambiar desde cuándo la ve el alumno. null = publicarla ya.
export const programarSemana = (id, desde) =>
  llamarFuncion("programar_semana", { p_semana: id, p_desde: desde || null });

// ---------- Planificador ----------

// La última semana de cada alumno, en una sola consulta: con eso se
// sabe a quién le falta la semana nueva sin abrirlos de a uno.
export async function ultimasSemanas() {
  let filas;
  try {
    filas = await leer("semanas", {
      select: "id,alumno_id,nombre,periodo,orden,creado_en,visible_desde",
      order: "orden.desc",
    });
  } catch (e) {
    filas = await leer("semanas", {
      select: "id,alumno_id,nombre,periodo,orden,creado_en", order: "orden.desc",
    });
  }
  const ultima = {};
  for (const w of filas) if (!(w.alumno_id in ultima)) ultima[w.alumno_id] = w;
  return ultima;
}

// Los hilos de los últimos días, para saber cuáles esperan respuesta:
// un hilo está pendiente si el último mensaje es del alumno.
export async function hilosRecientes(dias = 30) {
  const desde = new Date(Date.now() - dias * 864e5).toISOString();
  return leer("conversaciones", {
    select: "id,alumno_id,ejercicio_nombre,autor,autor_id,texto,foto,semana_nombre,reacciones,editado_en,creado_en",
    creado_en: `gte.${desde}`,
    order: "creado_en.asc",
  });
}

// Los videos que el coach ya miró. Es por persona: cada coach los suyos.
export async function videosVistos() {
  const filas = await leer("novedades_descartadas", {
    select: "clave", clave: "like.video:*",
  });
  return new Set(filas.map((f) => f.clave));
}

// Renombrar una semana o cambiarle el mes. Se corrige un error de
// carga sin tener que borrarla y rehacer todas sus sesiones.
export async function renombrarSemana(id, nombre, periodo) {
  const [fila] = await actualizar("semanas", { id }, { nombre, periodo });
  return fila;
}
export const borrarSesion = (id) => borrar("sesiones", { id });

// Duplicar: se arma la semana nueva y se copian las sesiones con la
// consigna intacta, pero SIN el registro del alumno. Los pesos de la
// semana anterior viajan como referencia.
export async function duplicarSemana(alumnoId, origen, nombre, periodo, visibleDesde = null) {
  const semana = await crearSemana(alumnoId, nombre, periodo, visibleDesde);
  for (const s of origen.sessions || []) {
    await crearSesion(semana.id, {
      name: s.name,
      // Sin día: el alumno las ubica de nuevo en la semana que viene.
      //
      // Copiar el día de la semana anterior las dejaba marcadas como
      // si ya las hubiera hecho: en su calendario aparecían el lunes,
      // miércoles y viernes que ya pasaron, y no había forma de
      // distinguir lo hecho de lo que falta.
      // El planificador sí puede elegir el día de la semana NUEVA.
      day: s.diaNuevo || null,
      fecha: s.fechaNueva || null,
      notaSemanal: s.notaNueva || null,
      musicaUrl: s.musicaNueva || null,
      estado: "Pendiente",
      exercises: s.exercises,
    });
  }
  return semana;
}


// La semana que viene, para varios alumnos de una.
//
// Con veinte alumnos, entrar a cada uno para crearles la semana son
// veinte idas y vueltas. Esto lo hace de corrido y avisa cuáles
// fallaron en vez de cortar en el primero.
export async function semanaSiguienteMasiva(alumnoIds, nombre, periodo, clonar, alAvanzar, visibleDesde = null) {
  const hechos = [];
  const fallados = [];

  for (const id of alumnoIds) {
    try {
      if (clonar) {
        // Su última semana, que es la que se clona.
        const [ultima] = await leer("semanas", {
          eq: { alumno_id: id }, select: "id,nombre,periodo",
          order: "orden.desc", limit: 1,
        });
        if (!ultima) {
          // Sin semanas previas no hay nada que clonar: se le crea
          // una vacía en vez de saltearlo.
          await crearSemana(id, nombre, periodo, visibleDesde);
          hechos.push({ id, vacia: true });
          continue;
        }
        const semanas = await cargarSemanas(id);
        const origen = semanas.find((w) => w.id === ultima.id);
        await duplicarSemana(id, origen || { sessions: [] }, nombre, periodo, visibleDesde);
      } else {
        await crearSemana(id, nombre, periodo, visibleDesde);
      }
      hechos.push({ id });
    } catch (e) {
      fallados.push({ id, error: e?.message || "error" });
    }
    // Se avisa después de cada uno, con éxito o sin él: lo que
    // importa es que la barra avance.
    if (alAvanzar) alAvanzar(hechos.length + fallados.length, id);
  }

  return { hechos, fallados };
}

// Asigna una plantilla a un alumno cualquiera, sin depender de qué
// alumno esté abierto en pantalla. Si el alumno todavía no tiene
// ninguna semana, se le crea una: es el caso del alumno recién
// registrado, que es justo cuando más se usa esto.
export async function asignarPlantilla(alumnoId, sesion) {
  const [ultima] = await leer("semanas", {
    eq: { alumno_id: alumnoId }, select: "id,nombre,periodo",
    order: "orden.desc", limit: 1,
  });

  let semana = ultima;
  if (!semana) {
    const hoy = new Date();
    const meses = ["ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO",
                   "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"];
    semana = await crearSemana(alumnoId, "Semana 1",
                               `${meses[hoy.getMonth()]} ${hoy.getFullYear()}`);
  }

  const fila = await crearSesion(semana.id, sesion);
  return { semana, sesion: fila, semanaCreada: !ultima };
}

// Compartir el entreno en el grupo suma EXP, una vez por sesión.
// Lo decide la base: la app solo avisa que se compartió.
export const registrarCompartido = (sesionId) =>
  llamarFuncion("registrar_compartido", { p_sesion: sesionId });


// Actividad de la semana para el inicio del coach.
//
// Devuelve TODO lo que tiene día asignado en la semana en curso de
// cada alumno, marcando qué está hecho y qué sigue pendiente. Antes
// solo traía lo completado, así que el coach no veía lo que había
// planificado y parecía que faltaban sesiones.
// Quién entrenó cada día de la semana.
//
// Lo resuelve la base porque un alumno no tiene permiso para leer las
// semanas de sus compañeros: armando esto en el navegador, al coach
// le funcionaba y al alumno le aparecía solo su propia actividad.
//
// La función devuelve nombre, foto y si completó. Nada más.
export async function cargarActividadSemana() {
  const porDia = await llamarFuncion("actividad_semana");
  const hoy = DIAS_SEMANA_API[(new Date().getDay() + 6) % 7];
  return {
    porDia: porDia && typeof porDia === "object" ? porDia : {},
    entrenaronHoy: ((porDia || {})[hoy] || [])
      .filter((x) => x.completado).map((x) => x.id),
  };
}

// Última carga registrada de cada ejercicio. Sirve como referencia
// mientras se planifica y mientras se entrena: el dato más útil no
// es lo que el coach cree, es lo que la persona movió la vez pasada.
// El peso y el RIR de la última vez que hizo cada ejercicio.
//
// Lo resuelve la base: antes se traían 400 filas de todos los alumnos
// y se filtraba en el navegador, y con veinte personas entrenando eso
// cubría poco más de una semana del grupo.
export async function ultimasCargas(alumnoId) {
  if (!alumnoId) return {};
  const r = await llamarFuncion("ultimas_cargas", { p_alumno: alumnoId });
  return r && typeof r === "object" ? r : {};
}

// ---------- Catálogo de ejercicios ----------

// Las categorías viven en su propia tabla: así una recién creada no
// desaparece por no tener ejercicios todavía, y su color se ve igual
// desde cualquier dispositivo.
export const cargarCategorias = () =>
  leer("categorias_ejercicios", { select: "id,nombre,color,orden", order: "orden.asc" });

// Devuelve { ok: false, en_sesiones, en_plantillas } si el ejercicio
// está en uso: la app avisa dónde en vez de un "no se puede" seco.
export const borrarEjercicio = (id) =>
  llamarFuncion("borrar_ejercicio", { p_id: id });

export const usoDelEjercicio = (nombre) =>
  llamarFuncion("uso_del_ejercicio", { p_nombre: nombre });

export const crearCategoriaEjercicios = async (nombre, color) => {
  const previas = await leer("categorias_ejercicios", {
    select: "orden", order: "orden.desc", limit: 1 });
  const [fila] = await insertar("categorias_ejercicios", {
    nombre: nombre.trim(), color, orden: (previas[0]?.orden ?? 0) + 1,
  });
  return fila;
};

export const cambiarColorCategoria = (id, color) =>
  actualizar("categorias_ejercicios", { id }, { color });

export async function cargarEjercicios() {
  const filas = await leer("ejercicios", {
    eq: { activo: true },
    select: "id,nombre,categoria,metrica_default,video_url,gif_url",
    order: "categoria.asc,nombre.asc",
  });

  const grupos = {};
  const medios = {};
  for (const e of filas) {
    (grupos[e.categoria] = grupos[e.categoria] || []).push(e.nombre);
    // El id se conserva aunque no tenga medios: hace falta para
    // poder borrar el ejercicio del catálogo.
    medios[e.nombre] = {
      id: e.id,
      videoUrl: e.video_url || "",
      gifUrl: e.gif_url || "",
    };
  }
  // Se suman las categorías que todavía no tienen ejercicios, con su
  // color. Sin esto, crear una categoría y recargar antes de cargarle
  // algo la haría desaparecer.
  const categorias = await leer("categorias_ejercicios", {
    select: "nombre,color,orden", order: "orden.asc" }).catch(() => []);
  const colores = {};
  for (const c of categorias) {
    colores[c.nombre] = c.color;
    if (!(c.nombre in grupos)) grupos[c.nombre] = [];
  }

  return {
    grupos: Object.entries(grupos).map(([label, items]) => ({ label, items })),
    medios,
    colores,
  };
}

// Crea o actualiza. Antes solo insertaba, así que editar un ejercicio
// ya existente fallaba en silencio: el nombre es único y la base
// rechazaba la fila nueva, dejando el gif solo en pantalla.
export async function crearEjercicio(datos) {
  const fila = {
    nombre: datos.nombre,
    categoria: datos.categoria,
    metrica_default: datos.metrica || "RIR",
    video_url: datos.videoUrl || null,
    gif_url: datos.gifUrl || null,
  };

  // Si cambió el nombre, se actualiza la fila vieja para no perder
  // el historial ni dejar un ejercicio huérfano.
  const buscar = datos.nombreAnterior || datos.nombre;
  const existentes = await leer("ejercicios", { eq: { nombre: buscar }, select: "id", limit: 1 });

  if (existentes.length) {
    return actualizar("ejercicios", { id: existentes[0].id }, fila);
  }
  return insertar("ejercicios", { ...fila, creado_por: miId() });
}


// ---------- Diagnóstico ----------
// Cuenta las filas de cada tabla. Existe para que el coach pueda
// verificar que lo que hace en la app llega a la base, sin tener
// que entrar al panel de Supabase a buscar tablas.

export async function diagnostico() {
  const tablas = ["perfiles", "ejercicios", "semanas", "sesiones",
                  "sesion_ejercicios", "exp_movimientos"];
  const filas = await Promise.all(tablas.map(async (t) => {
    try {
      const r = await leer(t, { select: "id", limit: 1000 });
      return { tabla: t, filas: r.length, ok: true };
    } catch (e) {
      return { tabla: t, filas: 0, ok: false, error: e.message };
    }
  }));

  let ultimaSemana = null;
  try {
    const [w] = await leer("semanas", {
      select: "nombre,periodo,creado_en", order: "creado_en.desc", limit: 1,
    });
    ultimaSemana = w || null;
  } catch { /* si falla, se muestra sin este dato */ }

  return { filas, ultimaSemana, momento: new Date().toLocaleTimeString("es-AR") };
}

// ---------- Alta de alumnos ----------
// El alumno se registra solo con mail y contraseña; el disparador de
// la base le crea el perfil con rol 'alumno'. No hace falta que el
// coach maneje contraseñas de nadie, que es como tiene que ser.

export async function invitarAlumno(nombre, apellido) {
  // Devuelve los datos que el coach le pasa al alumno para que se
  // registre. La cuenta la crea el propio alumno.
  return { nombre: nombre.trim(), apellido: apellido.trim() };
}


// ---------- Código de invitación ----------
// Solo el coach lo ve. Los alumnos lo escriben al registrarse y la
// base compara: la validación nunca pasa por el navegador.

export const codigoInvitacion = () => llamarFuncion("codigo_invitacion");

export const cambiarCodigoInvitacion = (codigo) =>
  llamarFuncion("cambiar_codigo_invitacion", { p_codigo: codigo });

export const regenerarCodigoInvitacion = () =>
  llamarFuncion("regenerar_codigo_invitacion");

// ---------- Insignias ----------
// Qué condiciones cumplió el alumno. Lo calcula la base leyendo lo
// que realmente entrenó: si lo hiciera el navegador, cualquiera
// podría reclamarlas todas editando la app.
export const insigniasLogradas = (alumnoId) =>
  llamarFuncion("insignias_logradas", { p_alumno: alumnoId || null });

// ---------- Propuestas de insignias ----------
export const cargarPropuestas = () =>
  leer("insignias_propuestas", {
    select: "id,autor_id,nombre,descripcion,estado,respuesta,creado_en",
    order: "creado_en.desc",
  });

export const proponerInsignia = (autorId, nombre, descripcion) =>
  insertar("insignias_propuestas", {
    autor_id: autorId, nombre: nombre.trim(), descripcion: descripcion.trim(),
  });

export const resolverPropuesta = (id, aprobar, respuesta = null) =>
  actualizar("insignias_propuestas", { id }, {
    estado: aprobar ? "aprobada" : "rechazada",
    respuesta,
    resuelto_en: new Date().toISOString(),
  });

// Al aprobar una propuesta se crea la insignia real, para que
// aparezca en la lista de todos.
export const crearInsigniaDesdePropuesta = async (propuesta) => {
  const previas = await leer("insignias", { select: "orden", order: "orden.desc", limit: 1 });
  const [fila] = await insertar("insignias", {
    codigo: `p${propuesta.id.slice(0, 8)}`,
    nombre: propuesta.nombre,
    descripcion: propuesta.descripcion,
    color: "#a78bfa",
    orden: (previas[0]?.orden ?? 0) + 1,
  });
  return fila;
};


// Tres estados: locked (no la tiene), pending (la solicitó),
// earned (el coach la aprobó). La EXP la otorga un disparador
// de la base al pasar a earned, así que la app no la toca.

export async function cargarInsignias() {
  // Con select "*": pedir columnas por nombre falla entero si alguna
  // no existe todavía —imagen_url y activa llegaron en la migración
  // 050— y la app se queda sin insignias en vez de sin una columna.
  let catalogo = [];
  let otorgadas = [];
  try { catalogo = await leer("insignias", { select: "*", order: "orden.asc" }); }
  catch { catalogo = []; }
  try {
    otorgadas = await leer("alumno_insignias", {
      select: "alumno_id,insignia_id,estado,progreso,otorgada_en",
    });
  } catch { otorgadas = []; }

  return {
    catalogo: Array.isArray(catalogo) ? catalogo : [],
    otorgadas: Array.isArray(otorgadas) ? otorgadas : [],
  };
}

// El alumno solicita la suya; queda pendiente hasta que el coach apruebe.
export const solicitarInsignia = (alumnoId, insigniaId) =>
  insertar("alumno_insignias", {
    alumno_id: alumnoId, insignia_id: insigniaId, estado: "pending",
  });

// Solo el coach. Al pasar a 'earned' la base suma la EXP sola.
export const resolverInsignia = (alumnoId, insigniaId, aprobar) =>
  aprobar
    ? actualizar("alumno_insignias", { alumno_id: alumnoId, insignia_id: insigniaId },
                 { estado: "earned" })
    : borrar("alumno_insignias", { alumno_id: alumnoId, insignia_id: insigniaId });

// ---------- Récords personales ----------

export const cargarRecords = (alumnoId) =>
  leer("records", {
    ...(alumnoId ? { eq: { alumno_id: alumnoId } } : {}),
    select: "id,alumno_id,ejercicio_nombre,tipo,valor,estado,logrado_en",
    order: "logrado_en.desc",
  });

export const proponerRecord = (alumnoId, ejercicio, tipo, valor) =>
  insertar("records", {
    alumno_id: alumnoId, ejercicio_nombre: ejercicio,
    tipo, valor: String(valor), estado: "pendiente",
  });

export const resolverRecord = (id, aprobar) =>
  actualizar("records", { id }, {
    estado: aprobar ? "aprobado" : "rechazado",
    revisado_por: miId(),
  });


// ---------- Librería de plantillas ----------
// Hasta ahora vivía solo en el navegador: al recargar volvían las de
// ejemplo y se perdía todo lo armado.

const CAMPOS_PLANTILLA_EJ = `
  id,orden,ejercicio_nombre,con_que,metrica,series,reps,rir_consigna,
  timer,biserie,modo,descanso,nota_coach,tecnica,tecnica_config,peso_sugerido,
  finalizador,seccion,mostrar_libras,bloque,ocultos,reps_por_lado
`.replace(/\s+/g, "");

export async function cargarLibreria() {
  const categorias = await leer("plantilla_categorias", {
    select: "id,nombre,metrica,orden", order: "orden.asc",
  });
  const plantillas = await leer("plantillas", {
    select: `id,categoria_id,nombre,tiene_timer,timer_trabajo,timer_descanso,timer_rondas,
             plantilla_ejercicios(${CAMPOS_PLANTILLA_EJ})`.replace(/\s+/g, ""),
    order: "creado_en.asc",
  });

  // Se arma la forma que ya usa la app: categorías con sus plantillas.
  const porCategoria = {};
  for (const c of categorias) porCategoria[c.id] = { ...c, templates: [] };
  const sueltas = [];

  for (const t of plantillas) {
    const plantilla = {
      id: t.id,
      name: t.nombre,
      hasTimer: t.tiene_timer,
      timerConfig: t.tiene_timer
        ? { work: t.timer_trabajo, rest: t.timer_descanso, rounds: t.timer_rondas }
        : null,
      exercises: (t.plantilla_ejercicios || [])
        .sort((a, b) => a.orden - b.orden)
        .map((e) => ({
          id: e.id,
          ejercicio: e.ejercicio_nombre,
          conQue: e.con_que || "",
          metric: e.metrica || "RIR",
          series: e.series || "",
          reps: e.reps || "",
          rirConsigna: e.rir_consigna || "",
          timer: e.timer || null,
      repsPorLado: !!e.reps_por_lado,
          biserie: e.biserie ?? null,
          modo: e.modo || "reps",
          descanso: e.descanso ?? null,
          notaCoach: e.nota_coach || "",
          tecnica: e.tecnica || "normal",
          tecnicaConfig: e.tecnica_config || null,
          pesoSugerido: e.peso_sugerido || "",
          mostrarLibras: !!e.mostrar_libras,
          finalizador: !!e.finalizador,
          seccion: e.seccion || (e.finalizador ? "finalizador" : "principal"),
          bloque: e.bloque || "",
          ocultos: Array.isArray(e.ocultos) ? e.ocultos : [],
          pesoUsado: "", rirTuyo: "", comentarios: "",
          pesoPrevia: "", rirPrevia: "", videoAlumno: "",
        })),
    };
    if (porCategoria[t.categoria_id]) porCategoria[t.categoria_id].templates.push(plantilla);
    else sueltas.push(plantilla);
  }

  const grupos = categorias.map((c) => ({
    id: c.id,
    name: c.nombre,
    metric: c.metrica,
    templates: porCategoria[c.id].templates,
  }));

  // Las que quedaron sin categoría van a la primera, que es "Sin categoría".
  if (sueltas.length && grupos.length) grupos[0].templates.push(...sueltas);
  return grupos;
}

const filasEjercicios = (plantillaId, exercises = []) =>
  exercises.filter((e) => e.ejercicio).map((e, i) => ({
    plantilla_id: plantillaId,
    orden: i,
    ejercicio_nombre: e.ejercicio,
    con_que: e.conQue || null,
    metrica: e.metric || "RIR",
    series: e.series || null,
    reps: e.reps || null,
    rir_consigna: e.rirConsigna || null,
    timer: e.timer || null,
    biserie: e.biserie ?? null,
    modo: e.modo || "reps",
    descanso: e.descanso ?? null,
    nota_coach: e.notaCoach || null,
    tecnica: e.tecnica || "normal",
    tecnica_config: e.tecnicaConfig || null,
    peso_sugerido: e.pesoSugerido || null,
    mostrar_libras: !!e.mostrarLibras,
    finalizador: !!e.finalizador,
    // La sección se guardaba solo en la sesión, no en la plantilla:
    // armabas la activación en la librería y al asignarla llegaba
    // todo como parte principal.
    seccion: e.seccion || (e.finalizador ? "finalizador" : "principal"),
    bloque: e.bloque || null,
    ocultos: Array.isArray(e.ocultos) ? e.ocultos : [],
    reps_por_lado: !!e.repsPorLado,
  }));

export async function crearPlantilla(categoriaId, plantilla) {
  const [fila] = await insertar("plantillas", {
    categoria_id: categoriaId,
    nombre: plantilla.name,
    tiene_timer: !!plantilla.hasTimer,
    timer_trabajo: plantilla.timerConfig?.work ?? null,
    timer_descanso: plantilla.timerConfig?.rest ?? null,
    timer_rondas: plantilla.timerConfig?.rounds ?? null,
  });
  const ejercicios = filasEjercicios(fila.id, plantilla.exercises);
  if (ejercicios.length) await insertar("plantilla_ejercicios", ejercicios);
  return fila;
}

// Actualizar reemplaza los ejercicios: reconciliar cuál cambió sería
// más código y más formas de equivocarse que borrarlos y reescribirlos.
export async function actualizarPlantilla(id, plantilla) {
  await actualizar("plantillas", { id }, {
    nombre: plantilla.name,
    tiene_timer: !!plantilla.hasTimer,
    timer_trabajo: plantilla.timerConfig?.work ?? null,
    timer_descanso: plantilla.timerConfig?.rest ?? null,
    timer_rondas: plantilla.timerConfig?.rounds ?? null,
  });
  await borrar("plantilla_ejercicios", { plantilla_id: id });
  const ejercicios = filasEjercicios(id, plantilla.exercises);
  if (ejercicios.length) await insertar("plantilla_ejercicios", ejercicios);
}

export const borrarPlantilla = (id) => borrar("plantillas", { id });

export const moverPlantilla = (id, categoriaId) =>
  actualizar("plantillas", { id }, { categoria_id: categoriaId });

export const crearCategoria = async (nombre, metrica = "RIR") => {
  const previas = await leer("plantilla_categorias", { select: "orden", order: "orden.desc", limit: 1 });
  const [fila] = await insertar("plantilla_categorias", {
    nombre, metrica, orden: (previas[0]?.orden ?? 0) + 1,
  });
  return fila;
};

// Al borrar una carpeta, sus plantillas vuelven a "Sin categoría".
export async function borrarCategoria(id, idSinCategoria) {
  if (idSinCategoria) {
    await actualizar("plantillas", { categoria_id: id }, { categoria_id: idSinCategoria });
  }
  return borrar("plantilla_categorias", { id });
}

// La lista de música. Se guarda en la sesión porque acompaña al
// entrenamiento entero, no a un ejercicio.
export const cargarMusicaDefault = async () => {
  const [f] = await leer("ajustes", {
    eq: { clave: "musica_default" }, select: "valor", limit: 1 });
  return f?.valor || "";
};

export const guardarMusicaSesion = (sesionId, url) =>
  actualizar("sesiones", { id: sesionId }, { musica_url: url || null });

const DIAS_SEMANA_API = ["Lunes", "Martes", "Miércoles", "Jueves",
                        "Viernes", "Sábado", "Domingo"];

// Los puntos que trae de la plataforma anterior. Se suman al que la
// app calcula sola, que sigue funcionando igual.
export const fijarPuntosPrevios = (alumnoId, puntos) =>
  llamarFuncion("fijar_puntos_previos", { p_alumno: alumnoId, p_puntos: puntos });

// ---------- Gestión de insignias ----------

// El catálogo completo con quién tiene cada una.
// Quién del grupo tiene cada insignia. Solo lo público: nombre, foto
// y qué ganó. Un alumno no puede leer las filas de sus compañeros.
export const insigniasDelGrupo = () => llamarFuncion("insignias_del_grupo");

export const insigniasConDuenos = () => llamarFuncion("insignias_con_duenos");

export const guardarInsignia = (datos) =>
  llamarFuncion("guardar_insignia", { p_datos: datos });

export const archivarInsignia = (id, activa) =>
  llamarFuncion("archivar_insignia", { p_insignia: id, p_activa: activa });

// Asignar o quitar a mano, sin esperar a que el alumno la reclame.
export const asignarInsignia = (alumnoId, insigniaId, dar) =>
  llamarFuncion("asignar_insignia",
                { p_alumno: alumnoId, p_insignia: insigniaId, p_dar: dar });

// ---------- Tableros ----------
export const tableroCoach = () => llamarFuncion("tablero_coach");
export const alumnosEnAlerta = () => llamarFuncion("alumnos_en_alerta");
export const tableroAlumno = (alumnoId) =>
  llamarFuncion("tablero_alumno", { p_alumno: alumnoId || null });

// ---------- Torneo ----------
// Una sesión suma al ranking solo si se compartió al grupo con foto:
// es el artículo 4 del reglamento.
// El resultado de un EMOM al fallo.
// La nota general del coach para una sesión.
// Por función y no escribiendo la tabla: los permisos de sesiones
// están cerrados para que el alumno no pueda cambiar su propia
// prescripción, y eso hacía fallar el guardado de la nota.
export const guardarNotaSemanal = (sesionId, texto) =>
  llamarFuncion("guardar_nota_semanal", { p_sesion: sesionId, p_texto: texto || "" });

// Quién le planificó a quién, estos días. Solo para coaches.
export const actividadCoaches = (dias = 7) =>
  llamarFuncion("actividad_coaches", { p_dias: dias });

// ---------- Coaches ----------
export const listaCoaches = () => llamarFuncion("lista_coaches");

// Nombrar o sacar un coach. Solo el administrador.
export const cambiarRol = (personaId, rol) =>
  llamarFuncion("cambiar_rol", { p_persona: personaId, p_rol: rol });

// Modo vacaciones: pausar el ranking mientras no se entrena.
export const cambiarVacaciones = (activo) =>
  llamarFuncion("cambiar_vacaciones", { p_activo: activo });

// Lo que pasó en el grupo estos días: pesos distintos al sugerido,
// comentarios, resultados de EMOM.
export const novedadesGrupo = (dias = 7) =>
  llamarFuncion("novedades_grupo", { p_dias: dias });

// ---------- Conversación por ejercicio ----------

// Los hilos de toda una sesión en una sola consulta: uno por tarjeta
// serían doce idas y vueltas al abrirla.
export const conversacionesSesion = (sesionId, semanas = 3) =>
  llamarFuncion("conversaciones_sesion", { p_sesion: sesionId, p_semanas: semanas });

export const conversacionCompleta = (alumnoId, ejercicio) =>
  llamarFuncion("conversacion_completa", { p_alumno: alumnoId, p_ejercicio: ejercicio });

// El autor lo decide la base según el rol, no la app.
export const enviarMensaje = ({ alumnoId, ejercicio, texto, foto, sesionId,
                                ejercicioId, semana }) =>
  llamarFuncion("enviar_mensaje", {
    p_alumno: alumnoId, p_ejercicio: ejercicio, p_texto: texto || "",
    p_foto: foto || null, p_sesion: sesionId || null,
    p_ejercicio_id: ejercicioId || null, p_semana: semana || null });

// Los mensajes sin leer, para el inicio. No dependen de una semana:
// el alumno abre la app y los ve, sin entrar a una sesión.
export const mensajesSinLeer = (dias = 14) =>
  llamarFuncion("mensajes_sin_leer", { p_dias: dias });

// Editar o borrar un mensaje propio del coach. La base controla que
// sea suyo (o de antes de que se guardara el autor).
export const editarMensaje = (mensajeId, texto) =>
  llamarFuncion("editar_mensaje", { p_mensaje: mensajeId, p_texto: texto });
export const borrarMensaje = (mensajeId) =>
  llamarFuncion("borrar_mensaje", { p_mensaje: mensajeId });

// Reaccionar a un mensaje. La misma otra vez la saca.
export const reaccionar = (mensajeId, emoji) =>
  llamarFuncion("reaccionar", { p_mensaje: mensajeId, p_emoji: emoji });

// Marcar una novedad como vista: no vuelve a aparecer.
export const descartarNovedad = (clave) =>
  llamarFuncion("descartar_novedad", { p_clave: clave });

// Qué cambió en esta semana respecto de la anterior.
export const novedadesSemana = (semanaId) =>
  llamarFuncion("novedades_semana", { p_semana: semanaId });

export const guardarEmom = (ejercicioId, bloques, reps) =>
  llamarFuncion("guardar_emom",
                { p_ejercicio: ejercicioId, p_bloques: bloques, p_reps: reps });

// Las últimas veces que hizo ese EMOM, para ver la tendencia.
export const progresionEmom = (alumnoId, ejercicio, cuantas = 5) =>
  llamarFuncion("progresion_emom",
                { p_alumno: alumnoId, p_ejercicio: ejercicio, p_cuantas: cuantas });

export const marcarCompartida = (sesionId) =>
  llamarFuncion("marcar_compartida", { p_sesion: sesionId });

export const miSemanaTorneo = (alumnoId) =>
  llamarFuncion("mi_semana_torneo", { p_alumno: alumnoId || null });

export const cargarTorneo = async () => {
  const [t] = await leer("torneo", { select: "nombre,desde,hasta,activo", limit: 1 });
  return t || null;
};

// Cambiar las sesiones semanales necesita el visto bueno del coach:
// en un torneo donde ese número es el denominador del puntaje,
// bajarlo en una mala semana convierte un punto en tres.
export const pedirCambioMeta = (sesiones, motivo) =>
  llamarFuncion("pedir_cambio_meta", { p_sesiones: sesiones, p_motivo: motivo || null });

export const resolverCambioMeta = (id, aprobar, respuesta) =>
  llamarFuncion("resolver_cambio_meta",
    { p_id: id, p_aprobar: aprobar, p_respuesta: respuesta || null });

export const cargarSolicitudesMeta = () =>
  leer("solicitudes_meta", {
    select: "id,alumno_id,actual,pedida,motivo,estado,respuesta,creado_en",
    order: "creado_en.desc",
  });

export const historialMeta = (alumnoId) =>
  leer("meta_semanal", {
    eq: { alumno_id: alumnoId },
    select: "sesiones,desde", order: "desde.desc",
  });

// ---------- Asistente del coach ----------
// Devuelve un borrador para revisar. No escribe nada en la base:
// lo que sale de acá lo decide Matías.
export async function pedirAsistente(tarea, contexto) {
  const r = await fetch("/.netlify/functions/asistente", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sesionActual()?.access_token || ""}`,
    },
    body: JSON.stringify({ tarea, contexto }),
  });
  const cuerpo = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(cuerpo.error || "El asistente no respondió");
  return cuerpo;
}

// ---------- Notificaciones ----------
export const cargarNotificaciones = () =>
  leer("notificaciones", {
    select: "id,tipo,titulo,detalle,vista,leida,creado_en,semana_id,sesion_id,ejercicio_id,ejercicio_nombre",
    order: "creado_en.desc", limit: 40,
  });

export const marcarLeida = (id) =>
  actualizar("notificaciones", { id }, { leida: true });

export async function marcarTodasLeidas() {
  const sinLeer = await leer("notificaciones", { eq: { leida: false }, select: "id" });
  await Promise.all(sinLeer.map((n) => actualizar("notificaciones", { id: n.id }, { leida: true })));
  return sinLeer.length;
}

export const borrarNotificacion = (id) => borrar("notificaciones", { id });

export const miPuesto = () => llamarFuncion("mi_puesto");

// ---------- Ranking ----------

export async function cargarRanking() {
  // Por función y no por la vista: la vista se ejecutaba con los
  // permisos de quien la consulta, y un alumno solo puede leer sus
  // propias sesiones. Cada uno se veía a sí mismo y a los demás en
  // cero. La función calcula del lado del servidor y devuelve solo
  // nombre, foto y puntos.
  const filas = await llamarFuncion("ranking_torneo");
  // El puntaje sale de la vista, que sigue el reglamento del torneo:
  // cumplimiento semanal, no cantidad de sesiones.
  return (Array.isArray(filas) ? filas : []).map((r) => ({
    id: r.alumno_id,
    name: `${r.nombre} ${r.apellido || ""}`.trim(),
    photo: r.foto_url,
    sessionsDone: r.sesiones_hechas,
    sessionsTotal: r.sesiones_totales,
    score: r.puntaje,
    puntosPrevios: r.puntos_previos || 0,
    puntosApp: r.puntos_app ?? r.puntaje,
    puntosSemana: r.puntos_semana || 0,
    hechasSemana: r.hechas_semana || 0,
    metaSemana: r.meta_semana || 0,
    exp: r.exp,
    nivel: r.nivel,
  }));
}

// ---------- EXP ----------
// Se escribe como movimientos, nunca tocando el total del perfil:
// el disparador de la base mantiene la suma sincronizada.

export const historialExp = (alumnoId) =>
  leer("exp_movimientos", {
    eq: { alumno_id: alumnoId },
    select: "motivo,puntos,detalle,creado_en",
    order: "creado_en.desc",
    limit: 50,
  });
